//@ts-check
import ky from "ky";
import { getfetchopts } from "$lib/utils/utils.js";
import { apiBase } from "$lib/env.js";

// Relative on the web; the app host inside the iOS app ($lib/env.js).
const basePath = apiBase();

// ---- Lists (product API — not built yet) ----
//
// The landing page currently renders hardcoded mock data. Wire these up as the
// endpoints land; shapes are sketched in guides/ARCH.md#api-design.
//
// export async function getLists() {
//   const fetchopts = await getfetchopts();
//   return ky.get(`${basePath}/api/lists`, fetchopts).json();
// }
//
// export async function createList(payload) {
//   const fetchopts = await getfetchopts({ json: payload });
//   return ky.post(`${basePath}/api/lists`, fetchopts).json();
// }

// ---- Auth (identity API; same-origin on the apex so the session cookie is sent) ----

/**
 * Create cross-domain login state and get the login-subdomain URL to redirect to.
 * @param {{ returnUrl?: string, device_id?: string, localOrigin?: string }} args
 */
export async function createLoginState({ returnUrl = "/", device_id, localOrigin } = {}) {
  // localOrigin is localhost-dev only: lets the API bounce the post-login callback
  // back to the local server.
  const json = { returnUrl, device_id, ...(localOrigin ? { localOrigin } : {}) };
  const fetchopts = await getfetchopts({ json });
  return ky.post(`${basePath}/api/auth/create-login-state`, fetchopts).json();
}

/**
 * Request a sign-in code, inside the iOS app (on the web the login subdomain does this). Resolves
 * with { ok: true } for any address while the state is valid, so it cannot be used to learn who
 * has an account; throws a 401 once the state has expired.
 * @param {{ email: string, state: string }} args
 */
export async function sendCode({ email, state }) {
  const fetchopts = await getfetchopts({ json: { email, state } });
  return ky.post(`${basePath}/api/auth/send-code`, fetchopts).json();
}

/**
 * Verify a sign-in code for a device, inside the iOS app: with `deviceName`, the identity API
 * answers with a device token rather than a cookie hand-off
 * (guides/AUTHENTICATION_FLOW.md#native-apps-device-tokens).
 * @param {{ email: string, code: string, state: string, deviceName: string }} args
 * @returns {Promise<{ device_token: string, device_id: string }>}
 */
export async function verifyDeviceCode({ email, code, state, deviceName }) {
  const fetchopts = await getfetchopts({ json: { email, code, state, deviceName }, noconsole: true });
  return ky.post(`${basePath}/api/auth/verify-code`, fetchopts).json();
}

/**
 * Exchange the one-time token from the login subdomain for the session cookie.
 * @param {{ exchange_token: string, state: string }} args
 */
export async function exchangeToken({ exchange_token, state }) {
  const fetchopts = await getfetchopts({ json: { exchange_token, state } });
  return ky.post(`${basePath}/api/auth/exchange`, fetchopts).json();
}

// ---- Shared Utility API (generic, cross-feature; /api/util/*) ----

/**
 * Poll the status of any background task.
 * @param {string} taskId
 * @returns {Promise<{ taskstate: { status: 'pending' | 'done' | 'error', fields?: object, error?: string } }>}
 */
export async function getBgTaskStatus(taskId) {
  const fetchopts = await getfetchopts();
  return ky.get(`${basePath}/api/util/bgtask/status/${taskId}`, fetchopts).json();
}

/**
 * Upload image bytes directly to S3 via the presigned URL. This is a BARE request — no auth
 * cookies/hooks (getfetchopts) — and the Content-Type MUST match the type the URL was signed for.
 * @param {{ uploadUrl: string, blob: Blob, contentType: string }} args
 */
export async function putToPresignedUrl({ uploadUrl, blob, contentType }) {
  await ky.put(uploadUrl, { body: blob, headers: { "content-type": contentType }, timeout: 60000 });
}
